import React, { useState, useRef, useEffect } from "react";
import "./CustomDropdown.css";

/**
 * Fully custom dropdown — no native <select>.
 *
 * Props:
 *   options             {value, label}[] | string[]  — list of options
 *   value               any                          — controlled value
 *   defaultValue        any                          — uncontrolled initial value
 *   onChange            (value) => void              — called with the resolved option value
 *   label               string                       — inline label rendered beside the trigger
 *   placeholder         string                       — trigger text when nothing is selected
 *   disabled            bool
 *   dataTestId          string
 *   className           string                       — appended to the root element's class
 *   style               object                       — inline style for root element
 *   triggerStyle        object                       — inline style for the trigger button
 *   menuStyle           object                       — inline style for the menu panel
 *   optionStyle         object                       — inline style applied to every option
 *   selectedOptionStyle object                       — extra inline style for the selected option
 *   activeColor         string (CSS value)           — overrides selected text / check color
 *   activeBg            string (CSS value)           — overrides selected option background
 *   multiple            bool                         — multi-select mode: value is an array and each
 *                                                      row renders a checkbox. Toggles edit a draft;
 *                                                      onChange receives the new array only when Done
 *                                                      is clicked (closing otherwise discards the draft)
 *   allOption           {value, label}               — (multiple only) adds a leading select-all /
 *                                                      deselect-all row; label is the trigger text
 *                                                      shown when every option is selected
 *   selectAllLabel      string                       — (multiple only) "all" row text when not all are selected
 *   deselectAllLabel    string                       — (multiple only) "all" row text when all are selected
 *   doneLabel           string                       — (multiple only) text of the Done button
 */
const CustomDropdown = ({
  options = [],
  value,
  defaultValue,
  onChange,
  label,
  placeholder = "Select…",
  disabled = false,
  dataTestId,
  className,
  style,
  triggerStyle,
  menuStyle,
  optionStyle,
  selectedOptionStyle,
  activeColor,
  activeBg,
  ariaLabel,
  triggerId,
  multiple = false,
  allOption,
  selectAllLabel = "Select all",
  deselectAllLabel = "Deselect all",
  doneLabel = "Done",
}) => {
  // Normalize options to { value, label, disabled }
  const normalized = options.map((o) =>
    typeof o === "string"
      ? { value: o, label: o, disabled: false }
      : { ...o, disabled: !!o.disabled },
  );

  // Controlled vs uncontrolled
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(
    defaultValue !== undefined ? defaultValue : multiple ? [] : "",
  );
  const resolvedValue = isControlled ? value : internalValue;

  const [isOpen, setIsOpen] = useState(false);

  // Multi-select: the committed selection drives the trigger; while the menu
  // is open, checkboxes edit a draft that is only emitted on Done.
  const [draftValues, setDraftValues] = useState([]);
  const committedValues = multiple
    ? (Array.isArray(resolvedValue) ? resolvedValue : []).map(String)
    : [];
  const selectableOptions = normalized.filter((o) => !o.disabled);
  const areAllSelected = (vals) =>
    selectableOptions.length > 0 &&
    selectableOptions.every((o) => vals.includes(String(o.value)));
  const isDraftAllSelected = multiple && areAllSelected(draftValues);
  const isDraftPartiallySelected =
    multiple && !isDraftAllSelected && draftValues.length > 0;
  const showAllOption = multiple && Boolean(allOption);

  // Rows rendered in the menu (the "all" row, when present, comes first)
  const menuItems = showAllOption
    ? [
        {
          value: allOption.value,
          label: isDraftAllSelected ? deselectAllLabel : selectAllLabel,
          disabled: false,
          isAll: true,
        },
        ...normalized,
      ]
    : normalized;

  const isItemSelected = (item) => {
    if (!multiple) return String(item.value) === String(resolvedValue);
    if (item.isAll) return isDraftAllSelected;
    return draftValues.includes(String(item.value));
  };

  // Opening a multi-select menu starts a fresh draft from the committed value
  const openMenu = () => {
    if (multiple) setDraftValues(committedValues);
    setIsOpen(true);
  };
  const toggleMenu = () => (isOpen ? setIsOpen(false) : openMenu());
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const isKeyboardRef = useRef(false);
  const itemRefs = useRef([]);
  const idRef = useRef(`cd-${Math.random().toString(36).slice(2, 9)}`);
  const menuId = `${idRef.current}-menu`;
  const labelId = `${idRef.current}-label`;

  const [activeIndex, setActiveIndex] = useState(-1);

  // Close menu when clicking outside the component
  useEffect(() => {
    if (!isOpen) return;
    const onMouseDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [isOpen]);

  // Close menu on Escape
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen]);

  // When menu opens, set the active index to the selected option or 0
  useEffect(() => {
    if (!isOpen) {
      setActiveIndex(-1);
      return;
    }

    // In multi-select mode, keep the current active row while toggling
    if (multiple) {
      setActiveIndex((i) => (i >= 0 ? i : 0));
      return;
    }

    const selIndex = normalized.findIndex(
      (o) => String(o.value) === String(resolvedValue),
    );
    setActiveIndex(selIndex >= 0 ? selIndex : 0);
  }, [isOpen, resolvedValue, normalized, multiple]);

  // Focus the active item when activeIndex changes (roving tabindex)
  useEffect(() => {
    if (!isOpen || activeIndex < 0) return;
    if (!isKeyboardRef.current) return;
    const el = itemRefs.current[activeIndex];
    if (el && typeof el.focus === "function") el.focus();
  }, [activeIndex, isOpen]);

  const handleListKeyDown = (e) => {
    isKeyboardRef.current = true;
    const len = menuItems.length;
    if (len === 0) return;

    const clamp = (n) => ((n % len) + len) % len;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => clamp(i + 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => clamp(i - 1));
      return;
    }
    if (e.key === "Home") {
      e.preventDefault();
      setActiveIndex(0);
      return;
    }
    if (e.key === "End") {
      e.preventDefault();
      setActiveIndex(len - 1);
      return;
    }
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      const idx = activeIndex >= 0 ? activeIndex : 0;
      const opt = menuItems[idx];
      if (opt && !opt.disabled) handleItemSelect(opt);
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setIsOpen(false);
      setTimeout(() => triggerRef.current && triggerRef.current.focus(), 0);
      return;
    }
  };

  const handleSelect = (optValue, optDisabled) => {
    if (optDisabled) return;
    if (!isControlled) setInternalValue(optValue);
    onChange && onChange(optValue);
    setIsOpen(false);
    // return focus to the trigger so keyboard users stay in flow
    setTimeout(() => triggerRef.current && triggerRef.current.focus(), 0);
  };

  // Multi-select: toggle a value in the draft, or select / deselect
  // everything for the "all" row. Nothing is emitted until Done.
  const handleToggle = (item) => {
    if (item.isAll) {
      setDraftValues(
        isDraftAllSelected ? [] : selectableOptions.map((o) => String(o.value)),
      );
      return;
    }
    const key = String(item.value);
    setDraftValues((prev) =>
      prev.includes(key) ? prev.filter((v) => v !== key) : [...prev, key],
    );
  };

  const handleDone = () => {
    if (draftValues.length === 0) return;
    // Emit original option values in options order so the array is stable
    const next = normalized
      .filter((o) => draftValues.includes(String(o.value)))
      .map((o) => o.value);
    if (!isControlled) setInternalValue(next);
    onChange && onChange(next);
    setIsOpen(false);
    setTimeout(() => triggerRef.current && triggerRef.current.focus(), 0);
  };

  const handleDoneKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      setIsOpen(false);
      setTimeout(() => triggerRef.current && triggerRef.current.focus(), 0);
    }
  };

  const handleItemSelect = (item) => {
    if (item.disabled) return;
    if (multiple) handleToggle(item);
    else handleSelect(item.value, item.disabled);
  };

  let triggerText;
  let hasSelection;
  if (multiple) {
    const selectedOptions = normalized.filter((o) =>
      committedValues.includes(String(o.value)),
    );
    hasSelection = selectedOptions.length > 0;
    if (allOption && areAllSelected(committedValues))
      triggerText = allOption.label;
    else if (hasSelection)
      triggerText = selectedOptions.map((o) => o.label).join(", ");
    else triggerText = placeholder;
  } else {
    const selectedOption = normalized.find(
      (o) => String(o.value) === String(resolvedValue),
    );
    triggerText = selectedOption?.label ?? placeholder;
    hasSelection = Boolean(selectedOption);
  }

  // Build aria attributes for the trigger: prefer explicit ariaLabel,
  // otherwise reference the visible label via aria-labelledby when present.
  const ariaProps = {};
  if (ariaLabel) ariaProps["aria-label"] = ariaLabel;
  else if (label) ariaProps["aria-labelledby"] = labelId;

  const rootClass = ["cd-root", disabled && "cd-disabled", className]
    .filter(Boolean)
    .join(" ");
  const triggerClass = ["cd-trigger", isOpen && "cd-trigger--open"]
    .filter(Boolean)
    .join(" ");
  const chevronClass = ["cd-chevron", isOpen && "cd-chevron--open"]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      ref={wrapperRef}
      className={rootClass}
      style={style}
      data-testid={dataTestId}
    >
      {label && (
        <span id={labelId} className="cd-label">
          {label}
        </span>
      )}

      <button
        type="button"
        id={triggerId}
        className={triggerClass}
        ref={triggerRef}
        onClick={() => {
          isKeyboardRef.current = false;
          toggleMenu();
        }}
        onKeyDown={(e) => {
          // Open/close with Enter/Space
          if (e.key === "Enter" || e.key === " ") {
            isKeyboardRef.current = true;
            e.preventDefault();
            toggleMenu();
            return;
          }
          // Open and move focus into the list with ArrowDown/ArrowUp
          if (e.key === "ArrowDown") {
            isKeyboardRef.current = true;
            e.preventDefault();
            if (!isOpen) openMenu();
            return;
          }
          if (e.key === "ArrowUp") {
            isKeyboardRef.current = true;
            e.preventDefault();
            if (!isOpen) openMenu();
            return;
          }
        }}
        aria-controls={isOpen ? menuId : undefined}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        {...ariaProps}
        disabled={disabled}
        style={triggerStyle}
        data-testid={dataTestId ? `${dataTestId}-trigger` : undefined}
      >
        <span
          className="cd-trigger__text"
          style={
            hasSelection
              ? {
                  color: activeColor || "var(--brand-color-1)",
                  fontWeight: 600,
                }
              : undefined
          }
        >
          {triggerText}
        </span>
        <svg
          className={chevronClass}
          viewBox="0 0 20 20"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M5 7.5L10 12.5L15 7.5"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen &&
        (() => {
          const list = (
            <ul
              id={menuId}
              className={multiple ? "cd-menu__list" : "cd-menu"}
              role="listbox"
              tabIndex={-1}
              aria-multiselectable={multiple || undefined}
              style={multiple ? undefined : menuStyle}
              data-testid={
                dataTestId && !multiple ? `${dataTestId}-menu` : undefined
              }
              onKeyDown={handleListKeyDown}
            >
              {menuItems.map((opt, idx) => {
                const isSelected = isItemSelected(opt);

                const liStyle = {
                  ...(optionStyle || {}),
                  ...(isSelected
                    ? {
                        ...(activeBg ? { backgroundColor: activeBg } : {}),
                        ...(activeColor ? { color: activeColor } : {}),
                        ...(selectedOptionStyle || {}),
                      }
                    : {}),
                };

                const isActive = idx === activeIndex;
                const optionId = `${idRef.current}-option-${idx}`;

                return (
                  <li
                    key={opt.value ?? idx}
                    id={optionId}
                    ref={(el) => (itemRefs.current[idx] = el)}
                    className={[
                      "cd-option",
                      isSelected && "cd-option--selected",
                      multiple && "cd-option--multi",
                      opt.isAll && "cd-option--all",
                      opt.disabled && "cd-option--disabled",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={opt.disabled || undefined}
                    tabIndex={isActive ? 0 : -1}
                    onClick={() => {
                      if (opt.disabled) return;
                      setActiveIndex(idx);
                      handleItemSelect(opt);
                    }}
                    onMouseEnter={() => {
                      isKeyboardRef.current = false;
                      setActiveIndex(idx);
                    }}
                    style={liStyle}
                    data-testid={
                      dataTestId
                        ? `${dataTestId}-option-${opt.value}`
                        : undefined
                    }
                  >
                    {multiple && (
                      <span
                        className={[
                          "cd-checkbox",
                          isSelected && "cd-checkbox--checked",
                          opt.isAll &&
                            isDraftPartiallySelected &&
                            "cd-checkbox--mixed",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        aria-hidden="true"
                        data-testid={
                          dataTestId
                            ? `${dataTestId}-checkbox-${opt.value}`
                            : undefined
                        }
                      >
                        {isSelected ? (
                          <svg viewBox="0 0 16 16" fill="none">
                            <path
                              d="M3.5 8.5L6.5 11.5L12.5 5"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        ) : opt.isAll && isDraftPartiallySelected ? (
                          <svg viewBox="0 0 16 16" fill="none">
                            <path
                              d="M4 8H12"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                          </svg>
                        ) : null}
                      </span>
                    )}
                    <span className="cd-option__label">{opt.label}</span>
                    {!multiple && isSelected && (
                      <svg
                        className="cd-option__check"
                        viewBox="0 0 16 16"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M3 8L6.5 11.5L13 5"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </li>
                );
              })}
            </ul>
          );
          if (!multiple) return list;
          return (
            <div
              className="cd-menu cd-menu--multi"
              style={menuStyle}
              data-testid={dataTestId ? `${dataTestId}-menu` : undefined}
            >
              {list}
              <div className="cd-menu__footer">
                <button
                  type="button"
                  className="cd-done"
                  onClick={handleDone}
                  onKeyDown={handleDoneKeyDown}
                  disabled={draftValues.length === 0}
                  data-testid={dataTestId ? `${dataTestId}-done` : undefined}
                >
                  {doneLabel}
                </button>
              </div>
            </div>
          );
        })()}
    </div>
  );
};

export default CustomDropdown;
